import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { authService } from '../services/authService'
import { Mail, AlertCircle, CheckCircle, RefreshCw, Edit3 } from 'lucide-react'
import { toast } from 'sonner'

export function VerifyEmailPage() {
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [showChangeEmail, setShowChangeEmail] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [isChangingEmail, setIsChangingEmail] = useState(false)

  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const { user, updateUser, logout } = useAuth()
  const navigate = useNavigate()

  // Si el usuario ya tiene el email verificado, redirigir
  useEffect(() => {
    if (user && user.emailVerified) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, navigate])

  // Si no hay usuario o no necesita verificar, redirigir
  if (!user) {
    navigate('/login', { replace: true })
    return null
  }

  if (user.emailVerified) {
    return null
  }

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return // Solo números

    const newCode = [...code]
    newCode[index] = value.slice(-1) // Solo un dígito
    setCode(newCode)

    // Auto-focus al siguiente input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    const newCode = [...code]
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i]
    }
    setCode(newCode)
    if (pastedData.length === 6) {
      inputRefs.current[5]?.focus()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const fullCode = code.join('')
    if (fullCode.length !== 6) {
      setError('Introduce el código completo de 6 dígitos')
      return
    }

    setIsLoading(true)

    try {
      const updatedUser = await authService.verifyEmail(fullCode)
      updateUser(updatedUser)
      toast.success('Email verificado correctamente')
      navigate('/dashboard', { replace: true })
    } catch (err: any) {
      setError(err.response?.data?.error || 'Código incorrecto')
      setCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    setIsResending(true)
    setError('')

    try {
      await authService.resendVerificationCode()
      toast.success('Código reenviado a tu email')
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al reenviar el código')
    } finally {
      setIsResending(false)
    }
  }

  const handleChangeEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setError('Introduce un email válido')
      return
    }

    setIsChangingEmail(true)

    try {
      const updatedUser = await authService.updateRecoveryEmail(newEmail)
      updateUser(updatedUser)
      toast.success('Email actualizado. Se ha enviado un nuevo código.')
      setShowChangeEmail(false)
      setNewEmail('')
      setCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cambiar el email')
    } finally {
      setIsChangingEmail(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="min-h-screen flex items-center justify-center gradient-viscofan dark:bg-dark-bg py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-secondary-500/20 dark:bg-secondary-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-400/20 dark:bg-primary-400/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-md w-full relative">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 backdrop-blur-xl border border-transparent dark:border-slate-700">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center mb-4">
              <Mail className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Verifica tu Email
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Hemos enviado un código de verificación a:
            </p>
            <p className="text-primary-600 dark:text-primary-400 font-medium mt-1">
              {user.email || user.recoveryEmail}
            </p>
          </div>

          {!showChangeEmail ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-xl p-4 flex items-center gap-3 animate-slide-in">
                  <AlertCircle className="h-5 w-5 text-error-500 flex-shrink-0" />
                  <p className="text-sm text-error-700 dark:text-error-400">{error}</p>
                </div>
              )}

              <div>
                <label className="label text-center block mb-3">
                  Introduce el código de 6 dígitos
                </label>
                <div className="flex justify-center gap-2" onPaste={handlePaste}>
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="w-12 h-14 text-center text-2xl font-bold input"
                      autoFocus={index === 0}
                    />
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || code.join('').length !== 6}
                className="w-full btn btn-primary flex items-center justify-center gap-2 py-3 text-base"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    Verificar Email
                  </>
                )}
              </button>

              <div className="flex flex-col gap-3 pt-4 border-t border-gray-200 dark:border-dark-border">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isResending}
                  className="w-full btn btn-secondary flex items-center justify-center gap-2"
                >
                  {isResending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent"></div>
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Reenviar Código
                </button>

                <button
                  type="button"
                  onClick={() => setShowChangeEmail(true)}
                  className="w-full btn btn-secondary flex items-center justify-center gap-2"
                >
                  <Edit3 className="h-4 w-4" />
                  Cambiar Email
                </button>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  Cerrar Sesión
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleChangeEmail} className="space-y-6">
              {error && (
                <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-xl p-4 flex items-center gap-3 animate-slide-in">
                  <AlertCircle className="h-5 w-5 text-error-500 flex-shrink-0" />
                  <p className="text-sm text-error-700 dark:text-error-400">{error}</p>
                </div>
              )}

              <div>
                <label htmlFor="newEmail" className="label">
                  Nuevo Email
                </label>
                <input
                  id="newEmail"
                  type="email"
                  required
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="input"
                  placeholder="tu@email.com"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Se enviará un nuevo código de verificación a este email.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowChangeEmail(false)
                    setNewEmail('')
                    setError('')
                  }}
                  className="flex-1 btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isChangingEmail}
                  className="flex-1 btn btn-primary flex items-center justify-center gap-2"
                >
                  {isChangingEmail ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  ) : (
                    'Cambiar Email'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-white/60 dark:text-gray-400 mt-8">
          © {new Date().getFullYear()} Viscofan España S.L.U. Todos los derechos reservados.
        </p>
      </div>
    </div>
  )
}
